import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, signal, OnDestroy } from '@angular/core';
import { LicenceService, LicenceEntityTypeApi } from '../../../core/service/licence-service/licence.service';
import { ApiService } from '../../../core/service/api-service/api-service';
import { API } from '../../../core/API';

export type LicenceEntityType = 'joueur' | 'dirigeant' | 'staff' | 'arbitre' | 'club';

const ENTITY_MAP: Record<LicenceEntityType, LicenceEntityTypeApi> = {
  joueur:    'player',
  dirigeant: 'manager',
  staff:     'staff',
  arbitre:   'referee',
  club:      'club',
};

@Component({
  selector: 'app-licence-dialog',
  standalone: false,
  templateUrl: './licence-dialog.component.html',
})
export class LicenceDialogComponent implements OnChanges, OnDestroy {
  @Input() item: any = null;
  @Input() entityType: LicenceEntityType = 'joueur';
  @Output() closed = new EventEmitter<boolean>(); // true = refresh list
  @Output() confirmed = new EventEmitter<any>();

  isSubmitting = signal(false);
  isDownloading = signal(false);
  isViewing = signal(false);
  isRegenerating = signal(false);
  regenerateSuccess = signal(false);
  generatedNumber = signal<string | null>(null);
  errorMessage = signal<string | null>(null);
  generatedPdfUrl = signal<string | null>(null);
  isPdfPending = signal(false);

  private pollTimer: any = null;
  private pollCount = 0;
  private readonly POLL_MAX = 15; // 15 × 2s = 30s max

  constructor(private licenceService: LicenceService, private apiService: ApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item) {
      this.stopPolling();
      this.generatedNumber.set(null);
      this.generatedPdfUrl.set(null);
      this.isPdfPending.set(false);
      this.errorMessage.set(null);
      this.regenerateSuccess.set(false);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPolling(demandeId: number): void {
    // A club licence has no card / PDF: nothing to wait for (polling it only produced 400 errors)
    if (ENTITY_MAP[this.entityType] === 'club') { this.isPdfPending.set(false); return; }
    this.isPdfPending.set(true);
    this.pollCount = 0;
    this.schedulePoll(demandeId);
  }

  private schedulePoll(demandeId: number): void {
    this.pollTimer = setTimeout(() => this.doPoll(demandeId), 2000);
  }

  private doPoll(demandeId: number): void {
    const apiType = ENTITY_MAP[this.entityType];
    const path = API.LICENCES.PDF_STATUS(demandeId, apiType);
    this.apiService.get(path).subscribe({
      next: (res: any) => {
        if (res?.ready && res?.url) {
          this.generatedPdfUrl.set(res.url);
          this.isPdfPending.set(false);
          this.stopPolling();
        } else {
          this.pollCount++;
          if (this.pollCount < this.POLL_MAX) {
            this.schedulePoll(demandeId);
          } else {
            this.isPdfPending.set(false);
            this.stopPolling();
          }
        }
      },
      error: () => {
        this.pollCount++;
        if (this.pollCount < this.POLL_MAX) {
          this.schedulePoll(demandeId);
        } else {
          this.isPdfPending.set(false);
          this.stopPolling();
        }
      },
    });
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  get canDownloadPdf(): boolean {
    return !!(this.item?.demande_status === 'LICENCE_GENERATED' || this.generatedNumber());
  }

  viewPdf(): void {
    if (!this.item?.demande_id) return;
    this.isViewing.set(true);
    this.errorMessage.set(null);
    const apiType = ENTITY_MAP[this.entityType];
    const path = API.LICENCES.DOWNLOAD_PDF(this.item.demande_id, apiType);
    this.apiService.getBlob(path).subscribe({
      next: (blob) => {
        this.isViewing.set(false);
        const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        window.open(blobUrl, '_blank');
      },
      error: () => {
        this.isViewing.set(false);
        this.errorMessage.set('Erreur lors de l\'ouverture du PDF.');
      },
    });
  }

  downloadPdf(): void {
    if (!this.item?.demande_id) return;
    this.isDownloading.set(true);
    this.errorMessage.set(null);
    const apiType = ENTITY_MAP[this.entityType];
    const path = API.LICENCES.DOWNLOAD_PDF(this.item.demande_id, apiType);
    this.apiService.getBlob(path).subscribe({
      next: (blob) => {
        this.isDownloading.set(false);
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `licence-${this.generatedNumber() || this.item.licence_number || this.item.demande_id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      },
      error: () => {
        this.isDownloading.set(false);
        this.errorMessage.set('Erreur lors du téléchargement du PDF. Puppeteer est peut-être indisponible sur le serveur.');
      },
    });
  }

  get title(): string {
    if (this.item?.demande_status === 'LICENCE_GENERATED' || this.generatedNumber()) {
      const map: Record<LicenceEntityType, string> = {
        joueur:    'Licence joueur',
        dirigeant: 'Licence dirigeant',
        staff:     'Licence entraîneur',
        arbitre:   'Licence arbitre',
        club:      'Licence club',
      };
      return map[this.entityType];
    }
    const map: Record<LicenceEntityType, string> = {
      joueur:    'Générer une licence joueur',
      dirigeant: 'Générer une licence dirigeant',
      staff:     'Générer une licence entraîneur',
      arbitre:   'Générer une licence arbitre',
      club:      'Générer une licence club',
    };
    return map[this.entityType];
  }

  get isActionable(): boolean {
    const t = this.item?.demande_type;
    const s = this.item?.demande_status;
    return (t === 'NEW' || t === 'RENEW' || t === 'TRANSFER' || t === 'EDIT') && s === 'FED_VALIDATED';
  }

  get fullName(): string {
    if (!this.item) return '—';
    const last = this.item.data_last_name || this.item.club_name || this.item.data_club_name || '';
    const first = this.item.data_first_name || '';
    return [last, first].filter(Boolean).join(' ') || '—';
  }

  get initials(): string {
    const f = this.item?.data_first_name?.charAt(0) || this.item?.data_club_name?.charAt(0) || '';
    const l = this.item?.data_last_name?.charAt(0) || '';
    return (f + l).toUpperCase() || '?';
  }

  demandeStatusColor(status: string): { text: string; bg: string } {
    const map: Record<string, { text: string; bg: string }> = {
      PENDING:           { text: '#92400e', bg: '#fef3c7' },
      LICENCE_GENERATED: { text: '#065f46', bg: '#d1fae5' },
      REJECTED:          { text: '#991b1b', bg: '#fee2e2' },
      CANCELLED:         { text: '#78716c', bg: '#f5f5f4' },
    };
    return map[status] || { text: '#78716c', bg: '#f5f5f4' };
  }

  regeneratePdf(): void {
    if (!this.item?.demande_id) return;
    this.isRegenerating.set(true);
    this.regenerateSuccess.set(false);
    this.errorMessage.set(null);
    const apiType = ENTITY_MAP[this.entityType];
    this.apiService.post(API.LICENCES.REGENERATE_PDF, { demande_id: this.item.demande_id, entity_type: apiType }).subscribe({
      next: (res: any) => {
        this.isRegenerating.set(false);
        if (res.success) {
          this.regenerateSuccess.set(true);
          setTimeout(() => this.regenerateSuccess.set(false), 4000);
        } else {
          this.errorMessage.set(res.message || 'Erreur lors de la régénération');
        }
      },
      error: (err: any) => {
        this.isRegenerating.set(false);
        this.errorMessage.set(err?.error?.message || 'Erreur serveur');
      },
    });
  }

  close(): void {
    this.stopPolling();
    const refresh = !!this.generatedNumber();
    this.generatedNumber.set(null);
    this.errorMessage.set(null);
    this.generatedPdfUrl.set(null);
    this.isPdfPending.set(false);
    this.closed.emit(refresh);
  }

  submit(): void {
    if (!this.item?.demande_id) return;
    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    const apiType = ENTITY_MAP[this.entityType];
    this.licenceService.generate(this.item.demande_id, apiType).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        if (res.success) {
          this.generatedNumber.set(res.licence_number);
          this.startPolling(this.item.demande_id);
        } else {
          this.errorMessage.set(res.message || 'Erreur lors de la génération');
        }
      },
      error: (err) => {
        this.isSubmitting.set(false);
        const msg = err?.error?.message || 'Erreur serveur';
        this.errorMessage.set(msg);
      },
    });
  }
}
